import React, { useEffect, useState } from 'react';
import { Box, Icon } from '@amzn/awsui-components-react-v3';
import Container from '@amzn/awsui-components-react-v3/polaris/container';
import Header from '@amzn/awsui-components-react-v3/polaris/header';
import Tabs from '@amzn/awsui-components-react-v3/polaris/tabs';
import SpaceBetween from '@amzn/awsui-components-react-v3/polaris/space-between';
import { IGenericObject } from "shared/programs/migration-acceleration-program/2024/fund-request/types/CommonTypes";
import { getPartnerInsights } from 'internal/util/services/aiba/AIBAService';

interface InsightState {
  loading: boolean;
  error: unknown | null;
  data: FundRequestAnalysis | null;
}

// Narrow type for what we actually consume. Extend as needed.
interface FundRequestAnalysis {
  RiskAssessment?: {
    HistoricalMetrics?: string[];
  };
  KeyInsights?: {
    Observations?: string[];
    ReviewerFocusAreas?: string[];
  };
  Recommendations?: {
    DataBackedSuggestions?: string[];
    AdditionalConsiderations?: string[];
  };
}

/**
 * usePartnerInsight — fetches the latest insight row for a partner.
 */
function usePartnerInsight(spmsId: string): InsightState {
  const [state, set] = useState<InsightState>({
    loading: true,
    error: null,
    data: null,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchInsights = async () => {
      if (!spmsId) {
        set({ loading: false, error: null, data: null });
        return;
      }

      set({ loading: true, error: null, data: null });
      
      try {
        console.log('Fetching insights for SPMS ID:', spmsId);
        const response = await getPartnerInsights(spmsId);
        
        console.log('Raw insight response:', response);

        if (response && typeof response === 'object' && 'errorType' in response) {
          console.error('Failed to parse JSON response');
        }

        let parsed = null;
        if (typeof response === 'string') {
          try {
            const jsonResponse = JSON.parse(response);
            parsed = jsonResponse.FundRequestAnalysis || null;
          } catch (parseError) {
            console.error('Failed to parse JSON response:', parseError);
          }
        } else if (response && typeof response === 'object') {
          parsed = (response as any).FundRequestAnalysis || null;
        }
        
        console.log('Parsed data:', parsed);
        
        if (!cancelled) {
          set({ loading: false, error: null, data: parsed });
        }
      } catch (err) {
        console.error('Error in usePartnerInsight:', err);
        if (!cancelled) {
          set({ loading: false, error: err, data: null });
        }
      }
    };

    fetchInsights();

    return () => {
      cancelled = true;
    };
  }, [spmsId]);

  return state;
}

const GenAiLabel = () => (
  <Box variant="awsui-gen-ai-label" margin={{ top: 'xs' }}>
    <Icon size="small" name="gen-ai" /> Generated by AI
  </Box>
);

const renderList = (items: string[]) => (
  <SpaceBetween size="xs">
    <ul style={{ margin: 0, paddingLeft: '20px' }}>
      {items.map((t: string, index: number) => (
        <li key={index} style={{ marginBottom: '8px' }}>
          {t}
        </li>
      ))}
    </ul>
    <GenAiLabel />
  </SpaceBetween>
);

interface PartnerProfileProps {
  data: IGenericObject;
  loading: boolean;
}

/**
 * PartnerProfile Component
 * — Gets SPMS ID from the same data prop as PartnerDetails
 */
export default function PartnerProfile({ data }: PartnerProfileProps) {
    const spmsId = data?.partnerDetails?.spmsId || data?.apnPartner?.partnerSpmsId;
    console.log('Extracted SPMS ID:', spmsId);
    
    const { loading: insightLoading, error, data: insightData } = usePartnerInsight(spmsId);

    const PartnerProfileBody = () => {
    if (insightLoading) return <Box>Loading…</Box>;
    if (error) return <Box color="text-status-error">Failed to load insights.</Box>;
    if (!insightData) return <Box>No insight found for this partner.</Box>;

    const {
      RiskAssessment: { HistoricalMetrics = [] } = {},
      KeyInsights: { Observations = [], ReviewerFocusAreas = [] } = {},
      Recommendations: {
        DataBackedSuggestions = [],
        AdditionalConsiderations = [],
      } = {},
    } = insightData;

    const tabs = [
      { label: 'Historical Metrics', list: HistoricalMetrics },
      { label: 'Observations', list: Observations },
      { label: 'Reviewer Focus Areas', list: ReviewerFocusAreas },
      { label: 'Data‑Backed Suggestions', list: DataBackedSuggestions },
      { label: 'Additional Considerations', list: AdditionalConsiderations },
    ];
const spmsId = data?.partnerDetails?.spmsId || data?.apnPartner?.partnerSpmsId;
    return (
      <Tabs
        tabs={tabs.map(({ label, list }, i) => ({
          label,
          id: `tab-${i}`,
          content: renderList(list),        
        }))}
      />
    );
  };

  return (
    <Container header={<Header variant="h2">Partner Profile Insights</Header>}>{PartnerProfileBody()}</Container>
  );
}
